import { Fragment } from 'react';
import type { ReactNode, VoidFunctionComponent } from 'react';

import { withTargetChecked } from '../../../utils';
import { FormLabel, LabeledCheckbox } from '../Forms';
import type { SelectOption, SelectOptionWithAttributes } from '../../../types';
import type { TooltipPlacement } from '../Tooltip';

import css from '../Forms/Checkbox.module.less';
// import css from './FilterCard.module.less';

type Props = {
  //  Label for the multiselect.
  label?: string;
  //  This is all of the checkboxes, with `label` used as the label and `value` as the key. Note you can use `selectOptionFactory` to create SelectOptions easily.
  options: SelectOptionWithAttributes[];
  //  Values are the values which are currently selected.
  values: SelectOption[];
  //  Change handler. Gets all selected options after the change takes effect.
  onChange: (values: SelectOption[]) => void;
  //  If the checkboxes are disabled or not.
  isDisabled?: boolean;
  //  ID for testing purposes.
  testId?: string;
  renderOption?: (option: SelectOption, checkbox: ReactNode) => ReactNode;
  tooltipPlacement?: TooltipPlacement;
};

const defaultRenderOption = (_option: SelectOption, checkbox: ReactNode) => checkbox;

/**
 * FilterCardCheckboxes renders a label with a set of checkboxes.
 * They use the SelectOption shape, where `label` is the text label
 * rendered beside the checkbox, and `value` is a unique key for that checkbox.
 *
 * This is intended to be used as a child of FilterCard, however it could
 * probably also be used outside that context.
 */
const FilterCardCheckboxes: VoidFunctionComponent<Props> = ({
  label,
  options,
  values,
  onChange,
  isDisabled,
  testId,
  renderOption = defaultRenderOption,
  tooltipPlacement,
}) => {
  const onCheckboxCheck = (option: SelectOption) => withTargetChecked(isChecked => {
    onChange(isChecked
      ? [...values, option] // Add to list
      : values.filter(({ value }) => value !== option.value)); // Remove from list
  });

  const getIsChecked = (value: string) => !!values.find(checkbox => checkbox.value === value);

  return (
    <div className={css.checkboxes} data-test-id={testId}>
      {label && <FormLabel label={label} />}
      {options.map((option) =>
        <Fragment key={option.value}>
          {renderOption(
            option,
            <LabeledCheckbox
              key={option.value}
              id={option.value}
              isChecked={getIsChecked(option.value)}
              label={option.label}
              tooltip={option.tooltip}
              tooltipPlacement={tooltipPlacement}
              onChange={onCheckboxCheck(option)}
              testId={testId ? `${testId}--${option.value}` : undefined}
              isDisabled={isDisabled || option.isDisabled}
              isRippleDisabled
              />)}
        </Fragment>,
      )}
    </div>
  );
};

export default FilterCardCheckboxes;
